import { usePrivy } from "@privy-io/react-auth";
import {
	AlertCircle,
	Archive,
	Layers,
	Loader2,
	Settings,
	Sparkles,
	Star,
	Trophy,
	Wallet,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import QuestCard, {
	type MyQuestSummary,
	type QuestLifecycle,
	type QuestProgress,
	type QuestWithSteps,
} from "@/components/QuestCard";
import { Button } from "@/components/ui/button";
import { usePointsActivity } from "@/hooks/usePointsActivity";
import { cn } from "@/lib/utils";
import { useShinkaiIdentityQuery } from "../hooks/shinkai-identity";
import { useActiveQuests } from "../hooks/useActiveQuests";
import { useMyQuests } from "../hooks/useMyQuests";

type QuestFilter = "all" | "available" | "completed" | "past" | "grants";

function formatActivityDate(value: string) {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "Unknown date";
	return date.toLocaleString();
}

export default function QuestsPage() {
	const { authenticated, ready } = usePrivy();
	const [questFilter, setQuestFilter] = useState<QuestFilter>("all");

	// Get admin status from query hook
	const { data: identityData } = useShinkaiIdentityQuery();
	const isAdmin = Boolean(identityData?.isAdmin);

	// Fetch My Quests — unified response with lifecycle, userState, earnedPoints per quest
	const { data: myQuestsData } = useMyQuests();
	const {
		data: pointsActivityData,
		isLoading: pointsActivityLoading,
		error: pointsActivityError,
	} = usePointsActivity({
		limit: 20,
		offset: 0,
		enabled: authenticated,
	});
	const pointsActivityEntries = pointsActivityData?.entries ?? [];

	const myQuests = useMemo(() => {
		if (!myQuestsData?.quests) return [];
		return [...myQuestsData.quests].sort((a, b) => {
			const dateA = new Date(a.quest.createdAt).getTime();
			const dateB = new Date(b.quest.createdAt).getTime();
			return dateB - dateA;
		});
	}, [myQuestsData]);

	// Build a lookup from questId → MyQuestSummary for merging user data onto public quest list
	const myQuestsByQuestId = useMemo(() => {
		const map = new Map<string, MyQuestSummary>();
		for (const summary of myQuests) {
			map.set(summary.quest.id, summary);
		}
		return map;
	}, [myQuests]);

	// Fetch public Quests list (for unauthenticated users and as source of steps data)
	const { data: quests, error: questsError, isLoading } = useActiveQuests();

	// --- Derived data ---

	// Separate quests by lifecycle (active vs ended only — no more "upcoming")
	const { activeQuests, endedQuests } = useMemo(() => {
		if (!quests) return { activeQuests: [], endedQuests: [] };

		const active: QuestWithSteps[] = [];
		const ended: QuestWithSteps[] = [];

		const sortByNewest = (a: QuestWithSteps, b: QuestWithSteps) =>
			new Date(b.quest.createdAt).getTime() -
			new Date(a.quest.createdAt).getTime();

		const now = new Date();
		for (const q of quests) {
			let lc: QuestLifecycle = "active";
			if (q.lifecycle) {
				lc = q.lifecycle;
			} else {
				const start = q.quest.startAt ? new Date(q.quest.startAt) : null;
				const end = q.quest.endAt ? new Date(q.quest.endAt) : null;
				if (start && start > now) lc = "upcoming";
				else if (end && end < now) lc = "expired";
			}

			// Skip upcoming quests entirely — only show active and expired
			if (lc === "upcoming") continue;
			if (lc === "expired") ended.push(q);
			else active.push(q);
		}

		return {
			activeQuests: active.sort(sortByNewest),
			endedQuests: ended.sort(sortByNewest),
		};
	}, [quests]);

	const pointsSummary = useMemo(() => {
		const questPointsFallback = myQuests.reduce(
			(sum, summary) => sum + summary.earnedPoints,
			0,
		);
		const questPoints =
			myQuestsData?.pointsSummary?.questPoints ?? questPointsFallback;
		const grantPoints = myQuestsData?.pointsSummary?.grantPoints ?? 0;
		const totalPoints =
			myQuestsData?.pointsSummary?.totalPoints ?? questPoints + grantPoints;
		return { totalPoints, questPoints, grantPoints };
	}, [myQuests, myQuestsData?.pointsSummary]);

	// Build QuestProgress map for rendering QuestCards
	const progressByQuestId = useMemo(() => {
		const map = new Map<string, QuestProgress>();
		myQuests.forEach((summary) => {
			map.set(summary.quest.id, {
				completedSteps: summary.completedSteps,
				totalSteps: summary.totalSteps,
				status:
					summary.userState === "maxed_out"
						? "completed"
						: ((summary.progress?.status as QuestProgress["status"]) ??
							"not_started"),
				completedStepIds: summary.completedStepIds,
			});
		});
		return map;
	}, [myQuests]);

	// User-centric quest buckets
	const { availableQuests, completedQuests, pastQuests } = useMemo(() => {
		const isDone = (questId: string) => {
			const mySummary = myQuestsByQuestId.get(questId);
			const state = mySummary?.userState ?? "not_started";
			return state === "completed" || state === "maxed_out";
		};

		// Available: active quests the user hasn't completed
		const available = activeQuests.filter((q) => !isDone(q.quest.id));

		// Completed: any quest (active or ended) the user has completed
		const completed = [
			...activeQuests.filter((q) => isDone(q.quest.id)),
			...endedQuests.filter((q) => isDone(q.quest.id)),
		];

		// Past: ended quests the user didn't complete (missed)
		const past = endedQuests.filter((q) => !isDone(q.quest.id));

		return {
			availableQuests: available,
			completedQuests: completed,
			pastQuests: past,
		};
	}, [activeQuests, endedQuests, myQuestsByQuestId]);

	// All quests combined (active first, then ended) for the "All" tab
	const allQuests = useMemo(
		() => [...activeQuests, ...endedQuests],
		[activeQuests, endedQuests],
	);
	const totalQuestPoints = useMemo(
		() => allQuests.reduce((sum, q) => sum + q.quest.points, 0),
		[allQuests],
	);

	// For unauthenticated users, show all quests; for authenticated, respect the filter
	const displayQuests = useMemo(() => {
		if (!authenticated) return allQuests;
		switch (questFilter) {
			case "all":
				return allQuests;
			case "available":
				return availableQuests;
			case "completed":
				return completedQuests;
			case "past":
				return pastQuests;
			case "grants":
				return [];
			default:
				return allQuests;
		}
	}, [
		authenticated,
		questFilter,
		allQuests,
		availableQuests,
		completedQuests,
		pastQuests,
	]);

	const filterCounts = useMemo(
		() => ({
			all: allQuests.length,
			available: authenticated ? availableQuests.length : activeQuests.length,
			completed: completedQuests.length,
			past: pastQuests.length,
			grants: pointsActivityEntries.length,
		}),
		[
			authenticated,
			allQuests,
			activeQuests,
			availableQuests,
			completedQuests,
			pastQuests,
			pointsActivityEntries.length,
		],
	);

	const totalVisibleQuests = allQuests.length;
	const headerStats = [
		{ label: "Total Points", value: pointsSummary.totalPoints },
		{
			label: "Quest Points",
			value: pointsSummary.questPoints,
			total: totalQuestPoints,
		},
		...(pointsSummary.grantPoints > 0
			? [{ label: "Grant Points", value: pointsSummary.grantPoints }]
			: []),
	];

	const emptyMessage = useMemo(() => {
		if (!authenticated) return "No challenges available right now.";
		switch (questFilter) {
			case "all":
				return "No challenges available right now.";
			case "available":
				return completedQuests.length > 0
					? "All challenges completed! Great job."
					: "No challenges available right now.";
			case "completed":
				return "No cards in your collection yet. Keep going!";
			case "past":
				return "No past challenges.";
			case "grants":
				return "No point grants yet.";
			default:
				return "No challenges available right now.";
		}
	}, [questFilter, authenticated, completedQuests.length]);

	return (
		<main className="grow px-6 font-inter">
			<div className="max-w-7xl w-full mx-auto space-y-10 py-8 pb-[150px]">
				{/* Header Section */}
				<section className="space-y-6 pb-6">
					<div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
						<div className="space-y-2">
							<h1 className="font-clash text-4xl font-medium leading-[1.1] tracking-tight text-white">
								Challenges
							</h1>
							<p className="text-base text-text-secondary max-w-lg">
								Complete quests by using Shinkai, creating agents, and exploring
								the ecosystem. Points unlock future benefits.
							</p>
						</div>

						{/* Stats & Admin Button */}
						<div className="flex items-center gap-6">
							<div className="flex items-center gap-6">
								{headerStats.map((stat, index) => (
									<div key={stat.label} className="flex items-center gap-6">
										{index > 0 && <div className="w-px h-10 bg-divider" />}
										<div className="text-center min-w-[110px]">
											<div className="flex items-baseline justify-center gap-1.5">
												<div
													className={cn(
														"font-semibold",
														index === 0
															? "text-3xl text-text-default"
															: "text-2xl text-text-default",
													)}
												>
													{stat.value.toLocaleString()}
												</div>
												{typeof stat.total === "number" && (
													<div className="text-sm text-text-tertiary">
														/ {stat.total.toLocaleString()}
													</div>
												)}
											</div>
											<div
												className={cn(
													"text-xs uppercase tracking-wider",
													index === 0
														? "text-text-secondary font-medium"
														: "text-text-tertiary",
												)}
											>
												{stat.label}
											</div>
										</div>
									</div>
								))}
							</div>
							{isAdmin && (
								<>
									<div className="w-px h-10 bg-divider" />
									<Link to="/rewards">
										<Button
											variant="outline"
											className="gap-2 border-divider hover:bg-bg-tertiary hover:border-brand/30"
										>
											<Settings className="w-4 h-4" />
											Manage
										</Button>
									</Link>
								</>
							)}
						</div>
					</div>
				</section>

				{/* Loading State */}
				{isLoading && (
					<div className="flex flex-col items-center justify-center py-16 gap-4">
						<Loader2 className="h-8 w-8 animate-spin text-brand" />
						<p className="text-sm text-text-tertiary">Loading quests...</p>
					</div>
				)}

				{/* Error State */}
				{questsError && (
					<div className="p-8 rounded-2xl border border-destructive/20 bg-destructive/5 text-center space-y-3">
						<div className="w-12 h-12 rounded-full bg-destructive/10 mx-auto flex items-center justify-center">
							<Trophy className="w-5 h-5 text-destructive" />
						</div>
						<div>
							<p className="text-text-default font-medium">
								Unable to load quests
							</p>
							<p className="text-sm text-text-secondary mt-1">
								Please try again later.
							</p>
						</div>
					</div>
				)}

				{/* Connect CTA for non-authenticated users */}
				{ready &&
					!authenticated &&
					!isLoading &&
					!questsError &&
					activeQuests.length > 0 && (
						<section className="rounded-2xl border border-brand/20 bg-linear-to-r from-brand/5 to-transparent p-6">
							<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
								<div className="flex items-start gap-4">
									<div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
										<Wallet className="w-6 h-6 text-brand" />
									</div>
									<div>
										<h3 className="font-semibold text-text-default mb-1">
											Connect to Track Your Progress
										</h3>
										<p className="text-sm text-text-secondary max-w-md">
											All quests are automatically available to you. Connect
											your wallet to track your progress and earn points.
										</p>
									</div>
								</div>
							</div>
						</section>
					)}

				{/* Quests Section */}
				{!isLoading &&
					!questsError &&
					(totalVisibleQuests > 0 || authenticated) && (
						<section className="space-y-6">
							{/* Tab filters (authenticated only) */}
							{authenticated && (
								<div className="flex items-center gap-2 flex-wrap">
									{(
										[
											{
												key: "all" as const,
												label: "All",
												icon: Layers,
											},
											{
												key: "available" as const,
												label: "Available",
												icon: Sparkles,
											},
											{
												key: "completed" as const,
												label: "My Collection",
												icon: Trophy,
											},
											{
												key: "grants" as const,
												label: "Point Grants",
												icon: Star,
											},
											...(filterCounts.past > 0
												? [
														{
															key: "past" as const,
															label: "Past",
															icon: Archive,
														},
													]
												: []),
										] as const
									).map(({ key, label, icon: Icon }) => {
										const isActive = questFilter === key;
										const count = filterCounts[key];
										return (
											<button
												key={key}
												type="button"
												onClick={() => setQuestFilter(key)}
												className={cn(
													"flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200",
													isActive
														? "bg-bg-quaternary text-text-default"
														: "bg-transparent border-white/5 text-text-tertiary hover:text-text-secondary hover:border-white/10",
												)}
											>
												<Icon
													className={cn(
														"w-3.5 h-3.5",
														isActive ? "text-cyan-400" : "text-text-tertiary",
													)}
												/>
												{label}
												{count > 0 && (
													<span
														className={cn(
															"text-[12px] font-bold tabular-nums ml-0.5",
															isActive ? "text-cyan-400" : "text-text-tertiary",
														)}
													>
														{count}
													</span>
												)}
											</button>
										);
									})}
								</div>
							)}

							{authenticated && questFilter === "grants" ? (
								<div className="space-y-4">
									{pointsActivityLoading && (
										<div className="flex items-center gap-2 text-sm text-text-tertiary py-4">
											<Loader2 className="w-4 h-4 animate-spin" />
											Loading point grants...
										</div>
									)}
									{pointsActivityError instanceof Error && (
										<div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 flex items-center gap-2">
											<AlertCircle className="w-4 h-4 text-destructive shrink-0" />
											<p className="text-sm text-destructive">
												{pointsActivityError.message}
											</p>
										</div>
									)}
									{!pointsActivityLoading &&
										!(pointsActivityError instanceof Error) &&
										pointsActivityEntries.length === 0 && (
											<div className="p-12 text-center border border-dashed border-divider rounded-2xl bg-bg-secondary/20">
												<div className="flex flex-col items-center gap-3">
													<div className="w-12 h-12 rounded-xl bg-bg-tertiary/50 flex items-center justify-center">
														<Star className="w-5 h-5 text-text-tertiary" />
													</div>
													<p className="text-text-secondary text-sm">
														No point grants yet.
													</p>
												</div>
											</div>
										)}
									{!pointsActivityLoading &&
										!(pointsActivityError instanceof Error) &&
										pointsActivityEntries.length > 0 && (
											<div className="rounded-xl border border-divider overflow-hidden bg-bg-secondary/30 shadow-sm">
												<div className="overflow-x-auto">
													<table className="w-full text-left text-sm">
														<thead className="bg-bg-tertiary/50 text-text-tertiary font-medium border-b border-divider">
															<tr>
																<th className="px-5 py-3.5 whitespace-nowrap font-medium text-xs uppercase tracking-wider w-28">
																	Points
																</th>
																<th className="px-5 py-3.5 whitespace-nowrap font-medium text-xs uppercase tracking-wider">
																	Reason / Description
																</th>
																<th className="px-5 py-3.5 whitespace-nowrap font-medium text-xs uppercase tracking-wider">
																	Source
																</th>
																<th className="px-5 py-3.5 text-right whitespace-nowrap font-medium text-xs uppercase tracking-wider w-32">
																	Date
																</th>
															</tr>
														</thead>
														<tbody className="divide-y divide-divider/50">
															{pointsActivityEntries.map((entry) => (
																<tr
																	key={entry.id}
																	className="hover:bg-bg-tertiary/30 transition-colors group"
																>
																	<td className="px-5 py-4 align-top">
																		<span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-brand/10 text-brand font-semibold text-xs border border-brand/20 shadow-sm shadow-brand/5">
																			+{entry.points.toLocaleString()}
																		</span>
																	</td>
																	<td className="px-5 py-4 align-top">
																		<span className="block font-medium text-text-default text-sm mb-0.5">
																			{entry.description?.trim() ||
																				"Point grant"}
																		</span>
																	</td>
																	<td className="px-5 py-4 align-top">
																		{entry.source ? (
																			<code className="text-[10px] text-text-tertiary bg-bg-tertiary/30 px-1.5 py-0.5 rounded font-mono border border-white/5">
																				{entry.source}
																			</code>
																		) : (
																			<span className="text-text-tertiary text-xs italic opacity-50">
																				-
																			</span>
																		)}
																	</td>
																	<td className="px-5 py-4 text-right text-text-tertiary text-xs whitespace-nowrap align-top">
																		{formatActivityDate(entry.grantedAt)}
																	</td>
																</tr>
															))}
														</tbody>
													</table>
												</div>
											</div>
										)}
								</div>
							) : displayQuests.length > 0 ? (
								<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
									{displayQuests.map((questData) => {
										const mySummary = myQuestsByQuestId.get(questData.quest.id);
										return (
											<QuestCard
												key={questData.quest.id}
												{...questData}
												progress={
													authenticated
														? progressByQuestId.get(questData.quest.id)
														: undefined
												}
												userState={
													authenticated ? mySummary?.userState : undefined
												}
												canProgressNow={
													authenticated ? mySummary?.canProgressNow : undefined
												}
											/>
										);
									})}
								</div>
							) : (
								<div className="p-12 text-center border border-dashed border-divider rounded-2xl bg-bg-secondary/20">
									<div className="flex flex-col items-center gap-3">
										<div className="w-12 h-12 rounded-xl bg-bg-tertiary/50 flex items-center justify-center">
											<Trophy className="w-5 h-5 text-text-tertiary" />
										</div>
										<p className="text-text-secondary text-sm">
											{emptyMessage}
										</p>
									</div>
								</div>
							)}
						</section>
					)}

				{/* Empty State (no quests at all) */}
				{!isLoading &&
					!questsError &&
					totalVisibleQuests === 0 &&
					!authenticated && (
						<div className="p-12 text-center border border-dashed border-divider rounded-2xl bg-bg-secondary/20">
							<div className="flex flex-col items-center gap-4">
								<div className="w-16 h-16 rounded-2xl bg-bg-tertiary/50 flex items-center justify-center">
									<Trophy className="w-7 h-7 text-text-tertiary" />
								</div>
								<div>
									<p className="text-text-secondary font-medium">
										No quests available right now
									</p>
									<p className="text-text-tertiary text-sm mt-1">
										New quests coming soon — stay tuned!
									</p>
								</div>
								<Link to="/">
									<Button variant="outline" className="mt-4">
										Back to Home
									</Button>
								</Link>
							</div>
						</div>
					)}
			</div>
		</main>
	);
}
